import React, { useEffect, useRef, useState } from 'react';
// Import react-router-dom hooks
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useClientDetails } from '@/hooks/useClientDetails';
import { useMealPlanGeneration } from '@/hooks/useMealPlanGeneration';
import { ClientActionBar } from '@/clients/details/legacy/components/ClientActionBar';
import { ClientBasicDetails } from '@/clients/details/legacy/components/ClientBasicDetails';
import { NutrientGuidelines } from '@/clients/details/legacy/components/NutrientGuidelines';
import { OverviewTabContent } from '@/clients/details/legacy/components/OverviewTabContent';
import { TabContent, TabContentRef } from '@/clients/details/legacy/components/TabContent';
import { TabsNavigation } from '@/clients/details/legacy/components/TabsNavigation';
import { LoadingState } from '@/components/clients/shared/LoadingState';
import { ErrorState } from '@/components/clients/shared/ErrorState';
import { NotFoundState } from '@/components/clients/shared/NotFoundState';
import { DeleteClientConfirmDialog } from '@/components/clients/DeleteClientConfirmDialog';
import { useDeleteClient } from '@/hooks/useDeleteClient';
import { isPendingDeletionClient } from '@/utils/client-account-deletion';
import ClientProfileSummary from '@/clients/details/legacy/components/ClientProfileSummary';
import FeedbackButton from '@/components/feedback/FeedbackButton';
import { FeedbackContext } from '@/components/feedback/FeedbackPanel';

const ClientDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const clientId = id || '';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>('overview');

  
  const {
    client,
    isLoading,
    error,
    isEditMode,
    editData,
    hasUnsavedChanges,
    hasClientDetailsChanges,
    isSavingChanges,
    enterEditMode,
    exitEditMode,
    saveAllChanges,
    updateEditData,
    refreshClientData,
    resetAllChangeTracking,
    setSavingState,
  } = useClientDetails(clientId);

  const {
    deleteTarget,
    isDeletingClient,
    isDeleteDialogOpen,
    requestDeleteClient,
    closeDeleteClient,
    confirmDeleteClient,
  } = useDeleteClient();

  const { handleCreateMealPlan } = useMealPlanGeneration();
  const tabContentRef = useRef<TabContentRef>(null);

  useEffect(() => {
    if (searchParams.get('mode') !== 'edit') {
      return;
    }

    if (isLoading || !client || isEditMode) {
      return;
    }

    enterEditMode();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('mode');
    setSearchParams(nextParams, { replace: true });
  }, [
    searchParams,
    setSearchParams,
    isLoading,
    client,
    isEditMode,
    enterEditMode,
  ]);

  const onCreateMealPlan = () => {
    if (client) {
      // Navigate to meal planning page with client pre-selected
      navigate(`/meal-planning?clientId=${client.id}`);
    }
  };

  const handleRequestDeleteClient = () => {
    if (!client) return;
    requestDeleteClient({
      id: clientId,
      name: client.fullName,
      status: client.status,
    });
  };

  const handleConfirmDeleteClient = async () => {
    const deleted = await confirmDeleteClient();
    if (deleted) {
      navigate('/clients', { replace: true });
    }
  };

  // Save function for client details only
  const handleSaveAllChanges = async () => {
    try {
      await saveAllChanges();
      exitEditMode(true);
    } catch (error: unknown) {
      console.error('Error saving changes:', error);
    }
  };

  // Exit edit mode
  const handleExitEditMode = () => {
    exitEditMode();
  };

  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error && !client) {
    return <ErrorState error={error} />;
  }

  // Client not found state
  if (!client) {
    return <NotFoundState />;
  }

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="w-full overflow-x-hidden p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7">
      <div className="max-w-full lg:max-w-7xl mx-auto space-y-8 font-manrope px-0">
        {/* Top Action Bar */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <ClientActionBar
              clientId={clientId}
              isEditMode={isEditMode}
              hasUnsavedChanges={hasUnsavedChanges}
              isSavingChanges={isSavingChanges}
              onEnterEditMode={enterEditMode}
              onExitEditMode={handleExitEditMode}
              onSaveChanges={handleSaveAllChanges}
              onCreateMealPlan={onCreateMealPlan}
              onDeleteClient={isPendingDeletionClient(client) ? undefined : handleRequestDeleteClient}
            />
          </div>
          <FeedbackButton
            clientId={clientId}
            label="Testing Feedback"
            defaultContext={FeedbackContext.CLIENT_ONBOARDING}
            title="Testing Feedback – Client Details"
            description='Please provide feedback on "Adding a client" and the overall Client Details section. Please specify the date of feedback. Multiple feedback entries allowed.'
            titlePlaceholder="e.g., Client profile creation issues"
            detailsPlaceholder="Describe the steps, date of feedback, and observations when adding a client or reviewing Client Details."
            submitLabel="Save Client Feedback"
            successDescription="Thanks for helping us improve Client Details."
          />
        </div>



        {/* Client Details and Nutrient Guidelines Row */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <ClientBasicDetails
            client={client}
            isEditMode={isEditMode}
            editData={editData}
            onDataChange={updateEditData}
          />
          <NutrientGuidelines client={client} refreshClientData={refreshClientData} />
        </div>

        {/* Client Profile Summary */}
        <div className={isEditMode ? "mb-8 ring-2 ring-primary/20 rounded-2xl bg-primary/2" : "mb-8"}>
          <ClientProfileSummary
            client={client}
            isEditMode={isEditMode}
            editData={editData}
            onDataChange={updateEditData}
          />
        </div>

        {/* Main content area with tabs */}
        <div className="space-y-6 min-w-0">
          <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="w-full min-w-0">
            <TabsNavigation activeTab={activeTab} onTabChange={setActiveTab} />

            {/* Tab Content */}
            <div className="min-w-0 w-full">
              <TabsContent value="overview" className="mt-0 space-y-6">
                <OverviewTabContent client={client} />
                </TabsContent>

              <TabContent 
                ref={tabContentRef}
                activeTab={activeTab} 
                client={client} 
                refreshClientData={refreshClientData}
              />
            </div>
          </Tabs>
        </div>
      </div>
    </div>
      </Paper>
      <DeleteClientConfirmDialog
        open={isDeleteDialogOpen}
        clientName={deleteTarget?.name || client.fullName}
        isPendingDeletion={deleteTarget?.isPendingDeletion ?? isPendingDeletionClient(client)}
        isDeleting={isDeletingClient}
        onOpenChange={(open) => {
          if (!open) closeDeleteClient();
        }}
        onConfirm={() => {
          void handleConfirmDeleteClient();
        }}
      />
    </Box>
  );
};

export default ClientDetailsPage;
